/**
 * Prepares an HTML string for auto-printing by injecting print-specific CSS
 * and a script to trigger the print dialog on load.
 *
 * @param originalHtml The HTML string to prepare.
 * @param title The title for the new HTML document.
 * @param printSettingsCss CSS string for @page rules (e.g., "@page { size: A3 landscape; margin: 10mm; }").
 * @returns The modified HTML string ready for printing.
 */
function prepareHtmlForAutoPrint(
  originalHtml,
  title = 'Print Document',
  printSettingsCss = '@page { size: A3 landscape; margin: 10mm; }', // Default to A3 Landscape
) {
  const parser = new DOMParser()
  // Attempt to parse as a full HTML document. If it's a fragment, it will be wrapped by body/html by the parser.
  const doc = parser.parseFromString(originalHtml, 'text/html')

  // 1. Ensure or set the <title>
  let titleTag = doc.querySelector('head > title')
  if (!titleTag) {
    titleTag = doc.createElement('title')
    // Prepend to head if head exists, otherwise, this might be problematic for pure fragments
    if (doc.head) {
      doc.head.insertBefore(titleTag, doc.head.firstChild)
    } else {
      // If it's a pure fragment without head, this won't add the title effectively
      // User should provide a full HTML structure or at least <html><head></head><body>...</body></html>
      console.warn(
        'HTML string seems to be a fragment without a <head>. Title might not be set correctly.',
      )
    }
  }
  if (titleTag) {
    titleTag.textContent = title
  }

  // 2. Inject @page print styles into the <head>
  const styleTagId = 'auto-print-page-settings'
  if (doc.head && !doc.getElementById(styleTagId)) {
    const styleTag = doc.createElement('style')
    styleTag.id = styleTagId
    styleTag.textContent = `
        @media print {
          ${printSettingsCss}
          /* Ensure backgrounds and images print by default in some browsers */
          body {
            -webkit-print-color-adjust: exact !important; /* Chrome, Safari, Edge */
            print-color-adjust: exact !important;       /* Standard */
          }
        }
      `
    doc.head.appendChild(styleTag)
  }

  // 3. Inject the auto-print script into the <body>
  const scriptTagId = 'auto-print-onload-script'
  if (doc.body && !doc.getElementById(scriptTagId)) {
    const scriptTag = doc.createElement('script')
    scriptTag.id = scriptTagId
    scriptTag.textContent = `
        (function() {
          // Ensure this script runs only once
          if (window.autoPrintScriptHasRun) {
            return;
          }
          window.autoPrintScriptHasRun = true;
  
          function triggerPrintDialog() {
            try {
              // A short timeout can help ensure content (especially images) is fully rendered.
              setTimeout(function() {
                window.print();
              }, 500); // 500ms delay, adjust if needed
            } catch (e) {
              console.error("Error triggering print dialog:", e);
              // Alert the user if printing cannot be initiated.
              // alert("Could not automatically trigger print dialog. Please use your browser's print function.");
            }
          }
  
          function N_waitForImagesAndPrint() {
              const images = Array.from(document.images);
              const totalImages = images.length;
              let loadedImagesCount = 0;
              let printHasBeenTriggered = false; // To prevent multiple calls
  
              function N_checkAndPrint() {
                  if (printHasBeenTriggered) return;
                  loadedImagesCount++;
                  if (loadedImagesCount >= totalImages) {
                      printHasBeenTriggered = true;
                      triggerPrintDialog();
                  }
              }
  
              if (totalImages === 0) {
                  // No images, trigger print dialog
                  triggerPrintDialog();
                  return;
              }
  
              images.forEach(img => {
                  if (img.complete) { // Image already loaded (e.g., cached)
                      N_checkAndPrint();
                  } else {
                      img.onload = N_checkAndPrint;
                      img.onerror = N_checkAndPrint; // Count error as "loaded" to not block printing indefinitely
                  }
              });
  
              // Safety timeout: if images take too long to load/error, print anyway
              setTimeout(() => {
                  if (!printHasBeenTriggered) {
                      console.warn("[AutoPrint] Timeout waiting for all images. Triggering print.");
                      printHasBeenTriggered = true;
                      triggerPrintDialog();
                  }
              }, 7000); // 7-second timeout
          }
  
          if (document.readyState === 'complete') {
              N_waitForImagesAndPrint();
          } else {
              window.addEventListener('load', N_waitForImagesAndPrint);
          }
        })();
      `
    doc.body.appendChild(scriptTag)
  }

  return doc.documentElement.outerHTML
}
export const openHtmlInNewTab = (htmlString, tabTitle = 'Print Document') => {
  const printCssForSettings = '@page { size: A3 landscape; margin: 12mm; }'
  const preparedHtml = prepareHtmlForAutoPrint(
    htmlString,
    tabTitle,
    printCssForSettings,
  )
  const blob = new Blob([preparedHtml], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const newTab = window.open(url, '_blank')
  if (newTab) {
    newTab.focus()
  }
}
/**
 * Opens an HTML string in a new tab and attempts to automatically trigger the print dialog
 * with print-specific CSS applied (e.g., for A3 Landscape).
 *
 * @param htmlString The HTML content to print.
 * @param tabTitle Optional title for the new tab/document.
 */
export const openHtmlInNewTabAndPrint = (
  htmlString,
  tabTitle = 'Print Document',
) => {
  // Define the desired print CSS here, e.g., A3 landscape
  const printCssForSettings = '@page { size: A3 landscape; margin: 12mm; }'

  const preparedHtml = prepareHtmlForAutoPrint(
    htmlString,
    tabTitle,
    printCssForSettings,
  )

  try {
    const blob = new Blob([preparedHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const newTab = window.open(url, '_blank')

    if (newTab) {
      newTab.focus()
      // Blob URL will be revoked by the browser when the tab is closed or the creating document unloads.
      // For more explicit control, you could try:
      // newTab.onbeforeunload = () => { URL.revokeObjectURL(url); };
      // However, this can be unreliable.
    } else {
      alert(
        'Failed to open new tab. Please check your pop-up blocker settings.',
      )
      URL.revokeObjectURL(url) // Clean up if the tab didn't open
    }
  } catch (error) {
    console.error(
      'Error opening HTML string in new tab for printing:',
      error.message,
      error.stack,
    )
    alert('An error occurred while trying to prepare the content for printing.')
  }
}

// --- Example Usage ---
/*
  const myHtmlToPrint = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      // Title will be overridden by the function parameter if provided
      <style>
        body { font-family: Arial, sans-serif; }
        h1 { color: steelblue; }
        p { margin: 5px 0; }
        .highlight { background-color: #f0f0f0; padding: 10px; border: 1px solid #ccc;}
        img { max-width: 100%; height: auto; display: block; margin-top: 10px; }
      </style>
    </head>
    <body>
      <h1>Document for A3 Landscape Printing</h1>
      <p>This is a test document that should open in a new tab and prompt for printing.</p>
      <div class="highlight">
        The print dialog should ideally show A3 Landscape as the default based on CSS @page rules.
        The user still has final control over these settings in the browser's print dialog.
      </div>
      <p>Some more content here to fill the page...</p>
      // Example of an image that the script will wait for:
      // <img src="https://via.placeholder.com/600x200.png?text=Sample+Image+For+Print" alt="Sample Image">
    </body>
    </html>
  `;
  
  // Typically, call this from a user-initiated event (e.g., button click)
  // function handlePrintButtonClick() {
  //   openHtmlInNewTabAndPrint(myHtmlToPrint, "My A3 Landscape Document");
  // }
  
  // const printButton = document.getElementById('printDocButton');
  // if (printButton) {
  //   printButton.addEventListener('click', handlePrintButtonClick);
  // }
  */
